<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { recordLabel } from '@/composables/useRecordLabel'

interface CrumbMeta {
  labelKey: string
  to?: string
}

interface Crumb {
  label: string
  to?: string
}

const route = useRoute()
const record = recordLabel()
const { t } = useI18n()

/**
 * Crumbs come from route meta, plus whatever the current view publishes about
 * the record it is showing. A crumb is never invented from the URL: a tenant
 * id is not a name, and showing a uuid as a breadcrumb helps nobody.
 *
 * Route meta carries catalogue *keys* (§6) — the records are built at module
 * load, so a literal label would not follow a locale switch.
 */
const crumbs = computed<Crumb[]>(() => {
  const fromMeta = route.meta['breadcrumb']
  const base = Array.isArray(fromMeta) ? (fromMeta as CrumbMeta[]) : []
  const translated: Crumb[] = base.map((crumb) =>
    crumb.to === undefined ? { label: t(crumb.labelKey) } : { label: t(crumb.labelKey), to: crumb.to },
  )
  return record.value === '' ? translated : [...translated, { label: record.value }]
})
</script>

<template>
  <nav v-if="crumbs.length > 0" :aria-label="$t('shell.breadcrumbLabel')" class="px-6 pt-3">
    <ol class="flex flex-wrap items-center gap-1 text-xs text-muted">
      <li class="flex items-center gap-1">
        <RouterLink to="/" class="rounded px-0.5 hover:text-link">
          {{ $t('shell.breadcrumbRoot') }}
        </RouterLink>
      </li>
      <li v-for="(crumb, index) in crumbs" :key="`${crumb.label}-${index}`" class="flex items-center gap-1">
        <span aria-hidden="true">/</span>
        <RouterLink v-if="crumb.to" :to="crumb.to" class="rounded px-0.5 hover:text-link">
          {{ crumb.label }}
        </RouterLink>
        <span v-else class="text-secondary" aria-current="page">{{ crumb.label }}</span>
      </li>
    </ol>
  </nav>
</template>
